import { Injectable } from '@angular/core';
import { ShiveRenderService } from '../../../../../../../../core/services/rendering/shive-render.service';
import {
  CSSActiveClass,
  CSSDisplayTimeBlockDuration,
  CSSNewClass,
  CSSPressedClass,
  CSSResizedClass,
} from '../../../../../../util/calendar-style.constants';
import { DateTimeHelper } from '../../../../../../util/date-time-helper';
import { TimeBlockCrudService } from '../../../../crud/time-block-crud.service';
import { TimeBlockItemBuilderService } from '../../../../generation/time-block-item-builder.service';
import { TimeBlockElementSelectorService } from '../../../../rendering/time-block-element-selector.service';
import { TimeBlockStructureService } from '../../../../time-block-structure/time-block-structure.service';
import { ITimeBlockComponentItem } from '../../../../types/time-block-component-item-types';
import { TimeBlockType } from '../../../../types/time-block-types';
import { DragHandlerService } from '../drag-handler.service';
import { DragPart } from '../drag-part';
import { TimeBlockDragResizeControllerService } from '../time-block-drag-resize-handling/time-block-drag-resize-controller.service';

@Injectable()
export class ResizeLaneSwitchService {
  public lastVisibleMouseLaneIndex = -1; // The lane index of the current resized time block.

  constructor(
    private readonly timeBlockItemBuilderService: TimeBlockItemBuilderService,
    private readonly timeBlockCrudService: TimeBlockCrudService,
    private readonly dragHandlerService: DragHandlerService,
    private readonly shiveRenderService: ShiveRenderService,
    private readonly timeBlockDragResizeControllerService: TimeBlockDragResizeControllerService,
    private readonly timeBlockStructureService: TimeBlockStructureService,
  ) {}

  /**
   * Handles lane switches when resizing an inner day week view time block part.
   * @return Returns true if lane switch should be skipped =>
   *    true if it is an active time block and it is resized beyond now indicator,
   *    false otherwise.
   */
  public handleLaneSwitch(
    visibleMouseLaneIndex: number,
    transformationTimeBlock: ITimeBlockComponentItem,
    minimumTimeBlockDuration: number,
  ): boolean {
    const laneDiff = visibleMouseLaneIndex - this.lastVisibleMouseLaneIndex;
    if (laneDiff === 0) {
      return false;
    }

    const transformationTimeBlockClone = transformationTimeBlock.clone(
      this.timeBlockItemBuilderService,
    );

    const cloneEnd = transformationTimeBlockClone.timeBlockModel.end;
    // If the end time is midnight, use end of day of the previous day.
    if (DateTimeHelper.isMidnight(transformationTimeBlockClone.timeBlockModel.end)) {
      transformationTimeBlockClone.timeBlockModel.end =
        DateTimeHelper.transformToNearlyMidnightIfNeeded(cloneEnd);
    }

    const endBeforeStart = this.updateTimeBlockDates(
      laneDiff,
      transformationTimeBlockClone,
      minimumTimeBlockDuration,
    );

    const newCloneEnd = transformationTimeBlockClone.timeBlockModel.end;
    const dragPart = this.timeBlockDragResizeControllerService.DragPart;
    if (
      (endBeforeStart === true || DateTimeHelper.isAfter(newCloneEnd, new Date())) &&
      transformationTimeBlockClone.timeBlockModel.isActive &&
      dragPart === DragPart.Bottom
    ) {
      return true;
    }

    this.timeBlockCrudService.removeTimeBlock(
      transformationTimeBlock,
      transformationTimeBlock.timeBlockModel.type,
    );
    this.timeBlockCrudService.insertTimeBlock(transformationTimeBlockClone);
    const parts = this.timeBlockStructureService.retrieveTimeBlockPart(
      transformationTimeBlockClone.id,
      transformationTimeBlockClone.timeBlockModel.type,
      -1,
    ) as ITimeBlockComponentItem[];

    const transformationPartNr =
      this.timeBlockDragResizeControllerService.DragPart === DragPart.Top ? 0 : parts.length - 1;

    this.timeBlockDragResizeControllerService.TransformationTimeBlock = parts[transformationPartNr];

    this.updateCSSOfParts(parts, transformationPartNr, endBeforeStart);

    this.lastVisibleMouseLaneIndex = visibleMouseLaneIndex;

    return false;
  }

  private updateTimeBlockDates(
    laneDiff: number,
    transformationTimeBlockClone: ITimeBlockComponentItem,
    minimumTimeBlockDuration: number,
  ): boolean {
    const origStart = transformationTimeBlockClone.timeBlockModel.start;
    const origEnd = transformationTimeBlockClone.timeBlockModel.end;
    let dragPart = this.timeBlockDragResizeControllerService.DragPart;

    // Determine whether we're modifying the start or end date.
    const selectedDateKey = dragPart === DragPart.Top ? 'start' : 'end';
    if (selectedDateKey === 'start') {
      transformationTimeBlockClone.timeBlockModel.start = this.shiftDate(origStart, laneDiff);
    } else {
      transformationTimeBlockClone.timeBlockModel.end = this.shiftDate(origEnd, laneDiff);
    }

    // Check if end date is before start date (i.e. resize handler was toggled).
    const newStart = transformationTimeBlockClone.timeBlockModel.start;
    const newEnd = transformationTimeBlockClone.timeBlockModel.end;

    const endBeforeStart = DateTimeHelper.isAfter(newStart, newEnd);

    // Check if end date is before start date (i.e. resize handler was toggled).
    if (endBeforeStart) {
      dragPart = dragPart === DragPart.Top ? DragPart.Bottom : DragPart.Top;
      // switch Dates
      transformationTimeBlockClone.timeBlockModel.start = newEnd;
      transformationTimeBlockClone.timeBlockModel.end = newStart;

      if (DateTimeHelper.isMidnight(transformationTimeBlockClone.timeBlockModel.end)) {
        transformationTimeBlockClone.timeBlockModel.end =
          laneDiff < 0
            ? origEnd
            : DateTimeHelper.addMinutes(
                transformationTimeBlockClone.timeBlockModel.end,
                minimumTimeBlockDuration,
              );
      }

      if (
        DateTimeHelper.isMidnight(
          DateTimeHelper.transformToExactlyMidnightIfNeeded(
            transformationTimeBlockClone.timeBlockModel.start,
          ),
        )
      ) {
        // If time is 23:59:59, transform it to 00:00:00 of next day.
        const start = DateTimeHelper.transformToExactlyMidnightIfNeeded(
          transformationTimeBlockClone.timeBlockModel.start,
          true,
        );

        transformationTimeBlockClone.timeBlockModel.start =
          laneDiff > 0 ? origStart : DateTimeHelper.subMinutes(start, minimumTimeBlockDuration);
      }

      this.timeBlockDragResizeControllerService.DragPart = dragPart;
    }
    return endBeforeStart;
  }

  private setDraggingTimeBlockPartCSSClasses(
    dragEdgeWasSwitched: boolean,
    dragPart: DragPart,
  ): void {
    if (dragEdgeWasSwitched) {
      this.dragHandlerService.setVerticalDragEdgeSwitchStyling();
    } else {
      let dragHandleHTMLElement = null;
      if (dragPart === DragPart.Top) {
        dragHandleHTMLElement = TimeBlockElementSelectorService.getTimeBlockFirstHandle(
          this.timeBlockDragResizeControllerService.TransformationTimeBlock,
        );
      } else if (dragPart === DragPart.Bottom) {
        dragHandleHTMLElement = TimeBlockElementSelectorService.getTimeBlockSecondHandle(
          this.timeBlockDragResizeControllerService.TransformationTimeBlock,
        );
      }

      if (!dragHandleHTMLElement) {
        return;
      }

      this.shiveRenderService.addClass(dragHandleHTMLElement, CSSPressedClass);
    }
  }

  private shiftDate(date: Date, diff: number): Date {
    return diff > 0
      ? DateTimeHelper.addDays(date, diff)
      : DateTimeHelper.subDays(date, Math.abs(diff));
  }

  private updateCSSOfParts(
    parts: ITimeBlockComponentItem[],
    selectedPartNr: number,
    dragEdgeWasSwitched: boolean,
  ): void {
    // Set current dragging time block part and CSS classes for all new dragging parts.
    parts.forEach((timeBlockPart) => {
      const timeBlockHTMLWrapper =
        TimeBlockElementSelectorService.getTimeBlockHTMLWrapper(timeBlockPart);

      this.shiveRenderService.addClass(timeBlockHTMLWrapper, CSSResizedClass);
      this.shiveRenderService.removeClass(timeBlockHTMLWrapper, CSSDisplayTimeBlockDuration);

      const dragPart = this.timeBlockDragResizeControllerService.DragPart;

      if (timeBlockPart.timeBlockModel.partNumber === selectedPartNr) {
        this.setDraggingTimeBlockPartCSSClasses(dragEdgeWasSwitched, dragPart);
        this.shiveRenderService.addClass(timeBlockHTMLWrapper, CSSDisplayTimeBlockDuration);
      }

      if (timeBlockPart.timeBlockModel.type === TimeBlockType.NonExistingBlock) {
        this.shiveRenderService.addClass(timeBlockHTMLWrapper, CSSNewClass);
      }
      this.shiveRenderService.addClass(timeBlockHTMLWrapper, CSSActiveClass);
    });
  }
}
